import BaseModalStore from '@src/components/modal-container/base-modal-store';
import { observable } from 'mobx';
import { autobind } from 'core-decorators';
import { noop } from '@src/utils/common-util';

/** 标签草稿容量约束：至少 1 个、至多 3 个（PRD 交互-6-1 / 交互-6-2 / 边界-6-1） */
const TAG_DRAFT_CAPACITY = 3;
const TAG_DRAFT_MINIMUM = 1;

/**
 * 标签编辑弹窗 Store：持有标签草稿与候选池快照（store-autonomy：草稿只在弹窗内读写）。
 * 与页面 Store 的协作通过构造注入完成：getter 取候选池/确认值，onConfirm 提交确认值；
 * 已确认标签与海报 ViewModel 归页面 Store，本 Store 不持有。
 */
class TagEditorModalStore extends BaseModalStore {
  /** 打开时从页面 Store 同步的候选池快照 */
  @observable candidateTags = [];

  /** 草稿：按点击顺序排列，容量 1~3 */
  @observable draftTags = [];

  /** 打开时的确认值快照，cancelDraft 的恢复来源 */
  snapshotTags = [];

  getCandidateTags = () => [];

  getConfirmedTags = () => [];

  /** 标签稀有度 getter（页面 Store 注入）：返回 1/2/3，映射缺失时由注入方兜底 1 */
  getTagRarity = () => 1;

  /** 标签达成条件描述 getter（页面 Store 注入，交互-6-10 描述区数据源）：返回 desc 文本，缺失兜底空串 */
  getTagDesc = () => '';

  onConfirm = noop;

  constructor(options = {}) {
    super(options);
    // 二次合并：子类字段初始化会覆盖 super 构造中的同名注入，需在字段初始化后重新落位
    Object.assign(this, options);
  }

  /**
   * 打开弹窗前由页面 Store 调用：从注入的 getter 拉取候选池与当前确认值，
   * 草稿与恢复快照都初始化为当前确认值（页面加载时默认为玩家标签池的前 3 个）。
   *
   * @returns {undefined}
   */
  @autobind
  syncSnapshot() {
    const confirmedTags = this.getConfirmedTags().slice(0, TAG_DRAFT_CAPACITY);
    this.candidateTags = this.getCandidateTags().slice();
    this.draftTags = confirmedTags.slice();
    this.snapshotTags = confirmedTags.slice();
    return undefined;
  }

  /**
   * 切换候选标签的草稿选中态：
   * 未选中则按点击顺序追加（已满 3 个不追加、无 Toast）；
   * 已选中则移除（最后一个不可移除，保持至少 1 个）。
   *
   * @param {string} tag 候选标签文本。
   * @returns {undefined}
   */
  @autobind
  toggleDraftTag(tag) {
    if (typeof tag !== 'string' || !tag) {
      return undefined;
    }

    if (this.draftTags.indexOf(tag) !== -1) {
      if (this.draftTags.length <= TAG_DRAFT_MINIMUM) {
        return undefined;
      }

      this.draftTags = this.draftTags.filter(item => item !== tag);
      return undefined;
    }

    if (this.draftTags.length >= TAG_DRAFT_CAPACITY) {
      return undefined;
    }

    this.draftTags = this.draftTags.concat(tag);
    return undefined;
  }

  /**
   * 候选项是否处于草稿选中态（弹窗 UI 选中 class 依据）。
   *
   * @param {string} tag 候选标签文本。
   * @returns {boolean} 是否已选。
   */
  isCandidateSelected(tag) {
    return this.draftTags.indexOf(tag) !== -1;
  }

  /**
   * 候选项稀有度（选中态边框取色依据）：注入 getter 结果收敛到合法档位 1/2/3。
   *
   * @param {string} tag 候选标签文本。
   * @returns {number} 稀有度 1/2/3。
   */
  rarityOf(tag) {
    const rarity = this.getTagRarity(tag);
    return rarity === 2 || rarity === 3 ? rarity : 1;
  }

  /**
   * 候选项达成条件描述（交互-6-10 描述区文案）：注入 getter 结果收敛为字符串，
   * 描述缺失（旧数据/未注入）按空串渲染。
   *
   * @param {string} tag 候选标签文本。
   * @returns {string} 达成条件描述；无数据时为空串。
   */
  descOf(tag) {
    const desc = this.getTagDesc(tag);
    return typeof desc === 'string' ? desc : '';
  }

  /**
   * 候选项是否禁用：已选满 3 个时其余候选禁用（边界-6-1，无 Toast）。
   *
   * @param {string} tag 候选标签文本。
   * @returns {boolean} 是否禁用。
   */
  isCandidateDisabled(tag) {
    return this.draftTags.length >= TAG_DRAFT_CAPACITY && this.draftTags.indexOf(tag) === -1;
  }

  /**
   * 确认草稿：至少 1 个标签才允许确认；经 onConfirm 把副本提交给页面 Store 后标准关闭。
   *
   * @returns {boolean} 是否确认成功（空草稿阻断时不关闭弹窗）。
   */
  @autobind
  confirmDraft() {
    if (this.draftTags.length < TAG_DRAFT_MINIMUM) {
      return false;
    }

    this.onConfirm(this.draftTags.slice());
    this.onClickClose();
    return true;
  }

  /**
   * 取消编辑：恢复打开时的确认值快照并标准关闭，不回调页面 Store。
   *
   * @returns {undefined}
   */
  @autobind
  cancelDraft() {
    this.draftTags = this.snapshotTags.slice();
    this.onClickClose();
    return undefined;
  }
}

export default TagEditorModalStore;
